const FALLBACK_FILENAME = 'TachoSAFE';
const CSV_EXTENSION = '.csv';
// The browser needs the blob URL to stay alive until the download has been picked up.
const REVOKE_OBJECT_URL_DELAY_IN_MS = 10000;

/**
 * Reads the file name out of a Content-Disposition header.
 *
 * The header can be `attachment; filename="report.csv"`, `attachment; filename=report.csv`,
 * `attachment; filename*=UTF-8''report.csv` or any combination of these (RFC 6266), so the raw
 * header must never be used as a download name: quotes, the `filename*=` part and the UTF-8 charset
 * prefix would end up in the file name and the extension would be broken.
 *
 * @returns the decoded file name, or undefined when the header carries none.
 */
export function parseContentDispositionFilename(contentDisposition: string): string {
    if (!contentDisposition) {
        return undefined;
    }
    const extendedMatch = /filename\*\s*=\s*([^;]+)/i.exec(contentDisposition);
    if (extendedMatch) {
        const value = extendedMatch[1].trim().replace(/^["']|["']$/g, '');
        // RFC 5987: charset'language'percent-encoded-name, e.g. UTF-8''report%20name.csv
        const charsetSeparatorIndex = value.indexOf("''");
        const encodedName = charsetSeparatorIndex >= 0 ? value.substring(charsetSeparatorIndex + 2) : value;
        try {
            return decodeURIComponent(encodedName) || undefined;
        } catch {
            // malformed percent encoding: keep the raw name instead of failing the download
            return encodedName || undefined;
        }
    }
    const plainMatch = /filename\s*=\s*([^;]+)/i.exec(contentDisposition);
    if (plainMatch) {
        return plainMatch[1].trim().replace(/^["']|["']$/g, '') || undefined;
    }
    return undefined;
}

/**
 * Replaces the extension of a file name (or adds one when it has none).
 *
 * The export endpoints answer with a `.csv` name even when the payload is converted to another
 * format, and their extension may be missing or upper case, so the resulting extension is enforced.
 */
export function replaceFileExtension(filename: string, extension: string): string {
    const baseName = (filename || '').replace(/\.[^./\\]+$/, '').trim();
    return `${baseName || FALLBACK_FILENAME}${extension}`;
}

/** Triggers a browser download of a blob under the given file name. */
export function downloadBlob(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    // Firefox and Safari only start the download when the anchor is part of the document
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => window.URL.revokeObjectURL(url), REVOKE_OBJECT_URL_DELAY_IN_MS);
}

export function downloadContent(response) {
    const filename = parseContentDispositionFilename(response.headers?.get('content-disposition'));
    downloadBlob(response.body, replaceFileExtension(filename, CSV_EXTENSION));
}
